import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ConfirmService } from '../../../shared/services/confirm.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { ConfirmDialogChoice } from '../../../shared/components/confirm-dialog.component';
import { LoadingDialogService } from '../../../../shared/ui/loading/loading-dialog.service';
import { PreinvoiceService } from '../../../../modules/invoice/preinvoice/preinvoice.service';
import { PreinvoiceModel } from '../../../../modules/invoice/preinvoice/preinvoice.model';
import { ConceptService } from '../../../../modules/concept/concept/concept.service';
import { documentConfirmDetails } from '../utils/document-confirm-details';
import { Utils } from '../../../../shared/utils';

export type ConvertToInvoiceMode = 'current' | 'previousMonth' | 'previousMonthPreviousYear';

export interface ConvertToInvoiceDocument {
  uid: string;
  kind: 'presupuesto' | 'albarán' | 'certificación';
  number?: string | null;
  receiverName?: string | null;
  total?: number | null;
}

const SUCCESS_MESSAGE: Record<ConvertToInvoiceDocument['kind'], string> = {
  'presupuesto': 'Presupuesto convertido en factura',
  'albarán': 'Albarán convertido en factura',
  'certificación': 'Certificación convertida en factura',
};

export interface ConvertToInvoiceBulk {
  uids: string[];
  kind: 'albarán' | 'certificación';
  total?: number | null;
  convert: (uids: string[]) => Promise<any>;
}

const PLURAL: Record<ConvertToInvoiceBulk['kind'], string> = {
  'albarán': 'albaranes',
  'certificación': 'certificaciones',
};

interface ConvertToInvoiceOptions {
  previousMonth: boolean;
  previousMonthPreviousYear: boolean;
}

@Injectable({ providedIn: 'root' })
export class ConvertToInvoiceService {

  constructor(
    private preinvoiceService: PreinvoiceService,
    private conceptService: ConceptService,
    private confirm: ConfirmService,
    private loading: LoadingDialogService,
    private notify: NotificationService,
    private router: Router,
  ) { }

  async open(doc: ConvertToInvoiceDocument): Promise<boolean> {
    let options: ConvertToInvoiceOptions;
    try {
      options = await this.loading.run('Comprobando opciones…', this.loadOptions(doc.uid));
    } catch {
      this.notify.error('No se pudieron cargar las opciones de conversión');
      return false;
    }

    const details = documentConfirmDetails(doc);

    const base = {
      title: 'Convertir a factura',
      subtitle: doc.number || undefined,
      icon: 'receipt',
      tone: 'info' as const,
      details,
      loadingText: 'Convirtiendo a factura…',
      errorText: `No se pudo convertir el ${doc.kind} en factura`,
    };

    const hasPastOptions = options.previousMonth || options.previousMonthPreviousYear;
    let converted: boolean;

    if (!hasPastOptions) {
      converted = await firstValueFrom(this.confirm.open({
        ...base,
        message: `¿Quieres convertir este ${doc.kind} en factura?`,
        confirmText: 'Convertir a factura',
        confirmIcon: 'receipt',
        action: () => this.publish(doc.uid, 'current'),
      }));
    } else {
      const choices: ConfirmDialogChoice[] = [];
      if (options.previousMonth) {
        choices.push({
          value: 'previousMonth',
          text: 'Con fecha del mes anterior',
          hint: 'La factura se emite en el mes anterior.',
          icon: 'history',
          action: () => this.publish(doc.uid, 'previousMonth'),
        });
      }
      if (options.previousMonthPreviousYear) {
        choices.push({
          value: 'previousMonthPreviousYear',
          text: 'Con fecha del mes anterior (año anterior)',
          hint: 'La factura se emite con fecha del año anterior.',
          icon: 'event',
          action: () => this.publish(doc.uid, 'previousMonthPreviousYear'),
        });
      }
      choices.push({
        value: 'current',
        text: 'Con fecha del mes actual',
        hint: options.previousMonth ? 'Después no podrás generar facturas con fecha del mes anterior.' : undefined,
        icon: 'check_circle',
        action: () => this.publish(doc.uid, 'current'),
      });

      converted = !!(await firstValueFrom(this.confirm.choose({
        ...base,
        message: '¿Con qué fecha quieres emitir la factura?',
        choices,
      })));
    }

    if (converted) {
      this.notify.ok(SUCCESS_MESSAGE[doc.kind]);
      this.router.navigate(['/invoice/issued']);
    }
    return converted;
  }

  async openBulk(bulk: ConvertToInvoiceBulk): Promise<boolean> {
    const count = bulk.uids ? bulk.uids.length : 0;
    const plural = PLURAL[bulk.kind];

    if (count === 0) {
      this.notify.warning(`Selecciona al menos un${bulk.kind === 'certificación' ? 'a' : ''} ${bulk.kind} para convertir en factura`);
      return false;
    }

    const converted = await firstValueFrom(this.confirm.open({
      title: 'Convertir en factura',
      subtitle: count === 1 ? `1 ${bulk.kind} seleccionad${bulk.kind === 'certificación' ? 'a' : 'o'}` : `${count} ${plural} seleccionad${bulk.kind === 'certificación' ? 'as' : 'os'}`,
      icon: 'receipt',
      tone: 'info',
      details: documentConfirmDetails({ total: bulk.total }),
      message: count === 1
        ? `¿Quieres convertir ${bulk.kind === 'certificación' ? 'la certificación seleccionada' : 'el albarán seleccionado'} en factura?`
        : `¿Quieres convertir ${bulk.kind === 'certificación' ? 'las' : 'los'} ${count} ${plural} seleccionad${bulk.kind === 'certificación' ? 'as' : 'os'} en factura?`,
      confirmText: 'Convertir en factura',
      confirmIcon: 'receipt',
      loadingText: 'Convirtiendo en factura…',
      errorText: `No se pudo convertir en factura`,
      action: () => bulk.convert(bulk.uids).then(response => {
        const message = response && response['message'];
        if (message) throw { error: { message } };
        const document = response && response['data'];
        if (document) this.assignSerialNumbers(document, false).catch(() => { });
      }),
    }));

    if (converted) {
      this.notify.ok(count === 1 ? SUCCESS_MESSAGE[bulk.kind] : `${count} ${plural} convertid${bulk.kind === 'certificación' ? 'as' : 'os'} en factura`);
      this.router.navigate(['/invoice/issued']);
    }
    return converted;
  }

  private loadOptions(uid: string): Promise<ConvertToInvoiceOptions> {
    const flag = (request: Promise<boolean>) => request
      .then(value => value === true)
      .catch(err => !!(err && err.error && err.error.data === true));

    return Promise.all([
      flag(this.preinvoiceService.canPublishPreInvoiceFifteenDaysSameYear(uid)),
      flag(this.preinvoiceService.canPublishPreInvoiceFifteenDaysChangeYear(uid)),
    ]).then(([previousMonth, previousMonthPreviousYear]) => ({ previousMonth, previousMonthPreviousYear }));
  }

  private async publish(uid: string, mode: ConvertToInvoiceMode): Promise<void> {
    const preinvoice = await this.preinvoiceService.loadData(uid);
    preinvoice.publish = true;
    preinvoice.preinvoicePublished = true;
    (preinvoice as any).projects = [];

    let result: PreinvoiceModel | string;
    if (mode === 'previousMonth') {
      result = await this.preinvoiceService.publishPreinvoiceFifteenDaysSameYear(uid, preinvoice);
    } else if (mode === 'previousMonthPreviousYear') {
      result = await this.preinvoiceService.publishPreinvoiceFifteenDaysChangeYear(uid, preinvoice);
    } else {
      result = await this.preinvoiceService.publish(uid, preinvoice);
    }

    if (typeof result === 'string') {
      throw { error: { message: result } };
    }

    this.assignSerialNumbers(preinvoice, true).catch(() => { });
  }

  private async assignSerialNumbers(preinvoice: PreinvoiceModel, decrementUnits: boolean): Promise<void> {
    const invoice = await this.preinvoiceService.loadData(preinvoice.uid);
    const concepts = await this.conceptService.getListSerialNumbers();
    if (!concepts || concepts.length === 0) return;

    for (const concept of concepts) {
      for (const line of preinvoice.invoiceLines || []) {
        if (!line.product || !line.product.includes(concept.name)) continue;

        const serialNumbers = await this.conceptService.getSerialNumbers(concept.uid);
        for (const serialNumber of serialNumbers) {
          if (!line.product.includes(serialNumber.serialNumber)) continue;

          serialNumber.invoiceUid = invoice.uid;
          serialNumber.invoiceNumber = invoice.invoiceNumber;
          await this.conceptService.modifySerialNumbers(serialNumber, concept.uid);
          if (!decrementUnits) continue;

          const current = await this.conceptService.loadData(concept.uid);
          current.units = current.units - 1;
          await this.conceptService.editData(concept.uid, current);
        }
      }
    }
  }
}
